import ApiClient from '@/api/ApiClient';

import type {
  LinkProviderRequest,
  PasswordRequest,
  ProviderUserLinkRecord,
  SortCriteria,
  SyncResultUser,
  Tag,
  User,
  UserDto,
  UserDto1,
  UserPublicDto,
  UserSyncRequest,
} from '../models';

export type GetUsersRequest = {
  departmentId?: string;
  groupId?: string;
  id?: string[];
  page?: number;
  size?: number;
  term?: string;
  userCreationType?: string;
};

export class UserClient {
  apiClient: ApiClient;

  constructor(apiClient?: ApiClient) {
    this.apiClient = apiClient || ApiClient.instance;
  }

  findTag(request: { tagName?: string; } = {}): Promise<Tag[]> {
    const { ...params } = request;
    return this.apiClient.get({
      path: '/api/identity/tags',
      params,
    });
  }

  createOrUpdateTag(request: { body?: Tag; } = {}): Promise<Tag> {
    const { body } = request;
    return this.apiClient.post({
      path: '/api/identity/tags',
      body,
    });
  }

  deleteTag(request: { id: number; }): Promise<void> {
    const { id } = request;
    return this.apiClient.del({
      path: `/api/identity/tags/${id}`,
    });
  }

  getUsers(request: GetUsersRequest = {}): Promise<User[]> {
    const { ...params } = request;
    return this.apiClient.get({
      path: '/api/identity/users',
      params,
    });
  }

  createUser(request: { sendNotification?: boolean; body?: User; } = {}): Promise<void> {
    const { body, ...params } = request;
    return this.apiClient.post({
      path: '/api/identity/users',
      body,
      params,
    });
  }

  getManagedUsers(request: { enabled?: boolean; sortCriteria?: SortCriteria[]; } = {}): Promise<UserDto[]> {
    const { ...params } = request;
    return this.apiClient.get({
      path: '/api/identity/users/managed',
      params,
    });
  }

  getPublic(): Promise<UserPublicDto[]> {
    return this.apiClient.get({
      path: '/api/identity/users/public',
    });
  }

  syncUsersByRequest(request: { body?: UserSyncRequest; } = {}): Promise<SyncResultUser> {
    const { body } = request;
    return this.apiClient.post({
      path: '/api/identity/users/sync',
      body,
    });
  }

  getUser(request: { userId: string; }): Promise<User> {
    const { userId } = request;
    return this.apiClient.get({
      path: `/api/identity/users/${userId}`,
    });
  }

  updateUser(request: { userId: string; body?: User; }): Promise<void> {
    const { userId, body } = request;
    return this.apiClient.put({
      path: `/api/identity/users/${userId}`,
      body,
    });
  }

  deleteUser(request: { userId: string; }): Promise<void> {
    const { userId } = request;
    return this.apiClient.del({
      path: `/api/identity/users/${userId}`,
    });
  }

  getLinks(request: { userId: string; }): Promise<ProviderUserLinkRecord[]> {
    const { userId } = request;
    return this.apiClient.get({
      path: `/api/identity/users/${userId}/links`,
    });
  }

  linkAccount(request: { userId: string; body?: LinkProviderRequest; }): Promise<void> {
    const { userId, body } = request;
    return this.apiClient.post({
      path: `/api/identity/users/${userId}/links`,
      body,
    });
  }

  deleteProvider(request: { providerName: string; userId: string; }): Promise<void> {
    const { providerName, userId } = request;
    return this.apiClient.del({
      path: `/api/identity/users/${userId}/links/${providerName}`,
    });
  }

  getUserManagers(request: { userId: string; levels?: number; }): Promise<User[]> {
    const { userId, ...params } = request;
    return this.apiClient.get({
      path: `/api/identity/users/${userId}/managers`,
      params,
    });
  }

  setUserPassword(request: { userId: string; body?: PasswordRequest; }): Promise<void> {
    const { userId, body } = request;
    return this.apiClient.post({
      path: `/api/identity/users/${userId}/password`,
      body,
    });
  }

  updateUserPassword(request: { userId: string; }): Promise<void> {
    const { userId } = request;
    return this.apiClient.post({
      path: `/api/identity/users/${userId}/recover`,
    });
  }

  syncUsers(request: { body?: UserDto1[]; } = {}): Promise<void> {
    const { body } = request;
    return this.apiClient.post({
      path: '/api/public/users/sync',
      body,
    });
  }
}

export default UserClient;
